import { arrayMove } from '@dnd-kit/sortable'
import type { Location } from '@mads/shared'

/**
 * The walking order a stocktake follows (spec W-04), as rearranged by dragging
 * the cards on the Locations screen (issue #27).
 *
 * Both ends of the drag need these rules — the screen turns a drop into the id
 * list it sends, and the cache rewrites itself from that list before the
 * request lands — so they live in one place rather than being written twice
 * and drifting apart.
 */

/**
 * The ids after `active` is dropped where `over` currently sits. Anything the
 * drop did not touch keeps its place; an id that is no longer in the list —
 * a location removed while the pointer was down — leaves the order untouched.
 */
export function moveInOrder(ids: number[], activeId: number, overId: number): number[] | null {
  if (activeId === overId) return null

  const from = ids.indexOf(activeId)
  const to = ids.indexOf(overId)
  if (from === -1 || to === -1) return null

  return arrayMove(ids, from, to)
}

/**
 * Locations arranged to match `ids`, applying the same rule the API does: the
 * ids named lead, in the order given, and anything unnamed keeps its relative
 * order behind them. Sharing the rule is what stops the order shown while the
 * request is in flight disagreeing with the one that comes back.
 */
export function inOrderOf(locations: Location[], ids: number[]): Location[] {
  const rank = new Map(ids.map((id, index) => [id, index]))

  return [...locations]
    .map((location, index) => ({ location, index }))
    .sort((a, b) => {
      // Ties — two unnamed locations — fall back to the order they arrived in;
      // `Array.prototype.sort` is stable, but saying so costs nothing.
      const rankA = rank.get(a.location.id) ?? ids.length + a.index
      const rankB = rank.get(b.location.id) ?? ids.length + b.index
      return rankA - rankB
    })
    .map(({ location }) => location)
}
